/**
 * API Path Constants
 * 
 * This file centralizes all API endpoints to ensure consistency between frontend calls
 * and backend routes. According to our architectural pattern:
 * - /public/* for public endpoints (no authentication required)
 * - /* for authenticated endpoints
 * 
 * Note: The baseURL (GO_SERVER_API_URL) already includes the '/api/' prefix,
 * so these paths should NOT include the '/api/' prefix.
 */

export const API_BASE = '';

export const AUTH = {
  LOGIN: `${API_BASE}/public/auth/login`,
  // Only responds when the backend is started with DEMO_LOGIN_ENABLED=true
  // (404s otherwise) — see AgriVision/api/routes.go.
  LOGIN_DEMO: `${API_BASE}/public/auth/login/demo`,
  REGISTER: `${API_BASE}/public/auth/register`,
  REFRESH: `${API_BASE}/public/auth/refresh`,
  // One-time cross-app hand-off code (landlord -> tenant app); see utils/unifiedSignIn.ts
  HANDOFF: `${API_BASE}/public/auth/handoff`,
  VERIFY: `${API_BASE}/public/auth/verify-token`,
  // Password reset endpoints
  REQUEST_PASSWORD_RESET: `${API_BASE}/public/auth/request-reset-password`,
  RESET_PASSWORD: `${API_BASE}/public/auth/reset-password`,
  // Logout endpoint (authenticated)
  LOGOUT: `${API_BASE}/public/auth/logout`
};

// User endpoints (authenticated)
export const USERS = {
  ALL: `${API_BASE}/users`,
  ME: `${API_BASE}/users/me`,
  BY_ID: (id: number) => `${API_BASE}/users/${id}`
};

// Plots endpoints (authenticated)
export const PLOTS = {
  ALL: `${API_BASE}/plots`,
  BY_ID: (id: number) => `${API_BASE}/plots/${id}`,
  STATUS: (id: number) => `${API_BASE}/plots/${id}/status`,
  ATTACHMENTS: (id: number) => `${API_BASE}/plots/${id}/attachments`,
};

// Contacts endpoints (authenticated)
export const CONTACTS = {
  ALL: `${API_BASE}/contacts`,
  BY_ID: (id: number) => `${API_BASE}/contacts/${id}`,
};

// Contracts endpoints (authenticated)
export const CONTRACTS = {
  ALL: `${API_BASE}/contracts`,
  BY_ID: (id: number) => `${API_BASE}/contracts/${id}`,
  SEARCH: `${API_BASE}/contracts/search`,
  SEND_EMAIL: (id: number) => `${API_BASE}/contracts/${id}/send-email`,
};

// Leads endpoints (authenticated)
export const LEADS = {
  ALL: `${API_BASE}/leads`,
  BY_ID: (id: number) => `${API_BASE}/leads/${id}`,
  CONTACT: `${API_BASE}/leads/contact`,
  UPDATE_STATUS: (id: number) => `${API_BASE}/leads/${id}/status`,
  SYNC: `${API_BASE}/leads/sync`,
};

// Proposal endpoints (authenticated)
export const PROPOSALS = {
  ALL: `${API_BASE}/proposals`,
  BY_ID: (id: number) => `${API_BASE}/proposals/${id}`,
  PUBLIC: `${API_BASE}/proposals/public`,
};

// Activity endpoints (authenticated)
export const ACTIVITIES = {
  ALL: `${API_BASE}/activities`,
  BY_ID: (id: number) => `${API_BASE}/activities/${id}`,
};

// Settings endpoints (authenticated)
export const SETTINGS = {
  EMAIL: `${API_BASE}/settings/email`,
  COMPANY: `${API_BASE}/settings/company`,
  EMAIL_TEST_CONNECTION: `${API_BASE}/settings/email/test-connection`,
  EMAIL_DISCONNECT: `${API_BASE}/settings/email/disconnect`,
};

// Attachments endpoints (authenticated)
export const ATTACHMENTS = {
  ALL: `${API_BASE}/attachments`,
  BY_ID: (id: number) => `${API_BASE}/attachments/${id}`,
};


// Notes endpoints (authenticated)
export const NOTES = {
  ALL: `${API_BASE}/notes`,
  BY_ID: (id: number) => `${API_BASE}/notes/${id}`,
};

// Invoices endpoints (authenticated)
export const INVOICES = {
  ALL: `${API_BASE}/invoices`,
  BY_ID: (id: number) => `${API_BASE}/invoices/${id}`,
};

// Crops endpoints (authenticated)
export const CROPS = {
  ALL: `${API_BASE}/crops`,
  BY_ID: (id: number) => `${API_BASE}/crops/${id}`,
  BY_PLOT: (plotId: number) => `${API_BASE}/plots/${plotId}/crops`,
};

// Status endpoints (authenticated)
export const STATUS = {
  ALL: `${API_BASE}/statuses`,
  BY_ID: (id: number) => `${API_BASE}/statuses/${id}`,
};

// Additional modules can be added as needed
